<template>
  <a-card class="quote-logistics">
    <v-col>
      <div class="quote-section-header">
        <div class="quote-section-title">Bus & Tour Guide</div>
        <v-btn
          x-small
          outlined
          color="primary"
          class="quote-section-action-btn"
          @click="openSupplierSettingDialog"
          :disabled="readonly || isSupplierSettingDialogDisabled()"
        >
          Supplier Setting
        </v-btn>
      </div>

      <div class="quote-section-block">
        <div class="quote-section-products">
          <table class="quote-section-table">
            <thead>
              <tr>
                <th class="quote-section-table__reorder"></th>
                <th>City</th>
                <th>Supplier</th>
                <th>Product</th>
                <th>Type</th>
                <th>Cost type</th>
                <th>Cost</th>
                <th class="quote-section-table__icons" colspan="4"></th>
                <th class="quote-section-table__unit">Unit</th>
                <th class="quote-section-table__program">Show in<br />program</th>
                <th class="quote-section-table__actions"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="data.logisticsCosts.length === 0" class="quote-section-table__empty-row">
                <td colspan="14">
                  <span class="quote-section-empty-label">No items added yet</span>
                </td>
              </tr>
              <tr
                v-for="(logisticsCost, index) in data.logisticsCosts"
                :key="index"
                class="quote-section-table__row quote-product-type-row"
                :style="getProductTypeCssVars(logisticsCost.productType)"
              >
                <td class="quote-section-table__reorder" align="center">
                  <v-btn icon x-small :disabled="readonly" @click="reorderItemDown(index)">
                    <v-icon small>mdi-arrow-down</v-icon>
                  </v-btn>
                  <v-btn icon x-small :disabled="readonly" @click="reorderItemUp(index)">
                    <v-icon small>mdi-arrow-up</v-icon>
                  </v-btn>
                </td>
                <td>
                  <f-text v-model="logisticsCost.supplierCity" smlabel :disabled="readonly || isCityDisabled(index)" />
                </td>
                <td>
                  <div class="quote-section-inline-field">
                    <autocomplete-grouped-free-text
                      :value="logisticsCost.supplier"
                      :items="
                        getSupplierItems(
                          data.period,
                          logisticsCost.productType,
                          getSupplierContextKey(logisticsCost.uuid),
                          logisticsCost.supplier
                        )
                      "
                      :loading="isSupplierLoading(getSupplierContextKey(logisticsCost.uuid))"
                      :has-more="hasMoreSuppliers(getSupplierContextKey(logisticsCost.uuid))"
                      class="flex-grow-1"
                      :disabled="readonly"
                      @input="onSupplierChanged(index, $event, logisticsCost.productType)"
                      @focus="onSupplierFocus(logisticsCost.uuid, logisticsCost.productType)"
                      @search="onSupplierSearch(logisticsCost.uuid, logisticsCost.productType, $event)"
                      @load-more="onSupplierLoadMore(logisticsCost.uuid, logisticsCost.productType)"
                    />
                    <v-btn
                      icon
                      x-small
                      @click="redirectToSupplier(logisticsCost.supplier)"
                      :disabled="readonly || logisticsCost.supplier?.value == null"
                    >
                      <v-icon small>mdi-open-in-new</v-icon>
                    </v-btn>
                  </div>
                </td>
                <td>
                  <div class="quote-section-inline-field">
                    <autocomplete-grouped-free-text
                      :value="logisticsCost.product"
                      :items="getProductItemsBySupplier(logisticsCost.supplier, logisticsCost.productType, data.period)"
                      :disabled="readonly || isProductNameDisabled(index)"
                      class="flex-grow-1"
                      @input="onProductChanged(data.period, index, $event)"
                      @focus="onProductFocus(logisticsCost.supplier)"
                    />
                    <v-btn
                      icon
                      x-small
                      @click="redirectToProduct(logisticsCost.product)"
                      :disabled="readonly || logisticsCost.product?.value == null"
                    >
                      <v-icon small>mdi-open-in-new</v-icon>
                    </v-btn>
                  </div>
                </td>
                <td>
                  <f-dropdown
                    hideClear
                    :value="logisticsCost.productType"
                    :items="getProductTypes"
                    :disabled="readonly || isProductTypeDisabled(index)"
                    @input="(v) => onProductTypeChanged(index, v, logisticsCost.supplier)"
                    class="quote-section-type-field"
                  />
                </td>
                <td>
                  <f-dropdown
                    hideClear
                    value="PG"
                    :items="logisticsCostTypeOptions"
                    disabled
                    class="quote-section-cost-type-field"
                  />
                </td>
                <td>
                  <div
                    class="d-flex justify-center align-center"
                    :class="{ 'quote-cost-field--missing': isProductCostMissing(logisticsCost) }"
                  >
                    <f-number
                      v-model="logisticsCost.productCost.value.default"
                      smlabel
                      :disabled="isPriceFieldDisabled(index)"
                      @blur="onCostBlur(logisticsCost)"
                    />
                  </div>
                </td>
                <td class="quote-section-table__icon-cell"></td>
                <td class="quote-section-table__icon-cell">
                  <v-tooltip top v-if="logisticsCost.productCost.confirm">
                    <template v-slot:activator="{ on }">
                      <v-icon small v-on="on" :color="logisticsCost.productCost.rateKind === 'CONTRACT' ? 'green' : ''">
                        mdi-check-circle-outline
                      </v-icon>
                    </template>
                    <span>Confirmed Rate</span>
                  </v-tooltip>
                </td>
                <td class="quote-section-table__icon-cell">
                  <v-tooltip top v-if="isSeasonalCost(logisticsCost.productCost.type)">
                    <template v-slot:activator="{ on }">
                      <v-btn color="grey" v-on="on" icon x-small>
                        <img src="@/assets/img/warning.png" class="quote-section-warning-icon" />
                      </v-btn>
                    </template>
                    <span>{{ seasonalPriceStartMessage }}</span>
                  </v-tooltip>
                </td>
                <td class="quote-section-table__icon-cell">
                  <v-btn
                    icon
                    x-small
                    :disabled="isLockButtonDisabled(index)"
                    @click="updateEditableCostStatus(data.period, index)"
                  >
                    <v-icon small>{{ getEditableCostIconButton(index) }}</v-icon>
                  </v-btn>
                </td>
                <td class="quote-section-table__unit" align="center">{{ (logisticsCost.days ?? []).length }}</td>
                <td class="quote-section-table__program">
                  <div class="quote-program-toggle">
                    <v-switch v-model="logisticsCost.isShowInProgram" dense hide-details :disabled="readonly" />
                  </div>
                </td>
                <td class="quote-section-table__actions" align="center">
                  <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon x-small v-bind="attrs" v-on="on" aria-label="Actions" :disabled="readonly">
                        <v-icon small>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense>
                      <v-list-item
                        :disabled="logisticsCost.isProductEditableCost"
                        @click="refreshProductCostType(data.period, index)"
                      >
                        <v-icon left small>mdi-refresh</v-icon>
                        <v-list-item-title>Reload Cost</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="removeCost(index)">
                        <v-icon left small>clear</v-icon>
                        <v-list-item-title>Delete</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </td>
              </tr>
              <tr class="quote-section-table__footer">
                <td colspan="14">
                  <v-btn small text color="primary" class="quote-section-add-line" :disabled="readonly" @click="addCost">
                    <v-icon left small>mdi-plus</v-icon>
                    Add new line
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </v-col>
  </a-card>
</template>

<script>
import { ProductMenu } from "@/config/menus";
import quoteUtils from "@/modules/quotes/components/utils";
import { isProductCostMissing } from "@/modules/quotes/components/quoteValidation";
import { getQuoteProductTypeCssVars } from "@/modules/quotes/components/quoteProductTypeColors";
import MONTHS from "@/config/MONTHS";
import money from "@/services/money";
import { TBD_SUPPLIER_IDS } from "@/config/TBD_SUPPLIERS";
import {
  emitSupplierDropdownEvent,
  getSupplierContextKey,
  getSupplierDropdownEntry,
  getSupplierEntriesByContext,
  hasMoreSuppliers,
  isSupplierLoading,
  mapGroupedSupplierItems,
} from "@/modules/quotes/components/supplierDropdown";

export default {
  props: {
    value: Object,
    supplierProductData: Object,
    readonly: {
      type: Boolean,
      default: false,
    },
    allowPriceEdits: {
      type: Boolean,
      default: false,
    },
    supplierDropdownState: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    data: {
      get() {
        return this.value;
      },
      set(newValue) {
        this.$emit("input", newValue);
      },
    },
    isPriceInteractionLocked() {
      return this.readonly && !this.allowPriceEdits;
    },
    getProductTypes() {
      return ProductMenu.filter((product) => ["TOUR", "TOUR_LEADER", "TRANS"].includes(product.module)).map(
        (product) => ({
          label: product.title,
          value: product.module,
        })
      );
    },
    seasonalPriceStartMessage() {
      const { startMonth, startDay } = this.data.period;
      const month = startMonth ?? 1;
      const day = startDay ?? 1;
      return `It uses the seasonal price from ${MONTHS[month - 1].label} ${day}st`;
    },
    environment() {
      return this.$store.getters["AccountStore/environment"];
    },
    currentCurrency() {
      return money.getCurrencySource(this.environment).currency;
    },
    logisticsCostTypeOptions() {
      return [{ label: "Per Group", value: "PG" }];
    },
  },
  methods: {
    getProductTypeCssVars(productType) {
      return getQuoteProductTypeCssVars(productType);
    },
    isProductCostMissing,
    onCostBlur(item) {
      if (this.isPriceInteractionLocked) return;
      item.productCost.value.default = quoteUtils.roundToTwoDecimals(item.productCost.value.default);
      if (this.allowPriceEdits) this.$emit("price-override-changed");
    },
    isSupplierSettingDialogDisabled() {
      return !quoteUtils.isSupplierMatchingEnabled(this.data.costs, this.data.logisticsCosts);
    },
    openSupplierSettingDialog() {
      if (this.readonly) return;
      this.$emit("openSupplierSettingDialog");
    },
    addCost() {
      if (this.readonly) return;
      this.data.logisticsCosts.push({
        uuid: quoteUtils.generateUUID(),
        productCost: {
          costType: "custom",
          currency: this.currentCurrency,
          value: {
            default: 0,
            single: null,
            twin: null,
            triple: null,
            quad: null,
          },
          confirm: false,
        },
        isProductEditableCost: true,
        isShowInProgram: true,
      });
    },
    removeCost(index) {
      if (this.readonly) return;
      const { logisticsCosts } = this.data;

      const logisticsCost = logisticsCosts[index];
      if (!logisticsCost || !logisticsCost.uuid) return;

      this.unlinkLogisticsCostUuid(logisticsCost.uuid);
      logisticsCosts.splice(index, 1);
    },
    reorderItemDown(index) {
      if (this.readonly) return;
      const nextItemIndex = index + 1;
      if (nextItemIndex >= this.data.logisticsCosts.length) return;

      this.swapItems(index, nextItemIndex);
    },
    reorderItemUp(index) {
      if (this.readonly) return;
      const previousItemIndex = index - 1;
      if (previousItemIndex < 0) return;

      this.swapItems(index, previousItemIndex);
    },
    swapItems(itemIndex1, itemIndex2) {
      const logisticsCosts = [...this.data.logisticsCosts];
      const temp = logisticsCosts[itemIndex1];
      logisticsCosts[itemIndex1] = logisticsCosts[itemIndex2];
      logisticsCosts[itemIndex2] = temp;

      this.data = { ...this.data, logisticsCosts };
    },
    isCityDisabled(index) {
      const supplier = this.data.logisticsCosts[index]?.supplier;
      if (quoteUtils.isTBDSupplier(supplier?.value)) return false;
      return !supplier || supplier.label == null || supplier.value != null;
    },
    isProductNameDisabled(index) {
      return this.data.logisticsCosts[index]?.supplier == null;
    },
    isProductTypeDisabled(index) {
      return this.data.logisticsCosts[index].product?.value != null;
    },
    isCostDisabled(index) {
      return !this.data.logisticsCosts[index].isProductEditableCost;
    },
    isPriceFieldDisabled(index) {
      return this.isPriceInteractionLocked || this.isCostDisabled(index);
    },
    isEditableCostDisabled(index) {
      return this.data.logisticsCosts[index].product?.value == null;
    },
    isLockButtonDisabled(index) {
      return this.isPriceInteractionLocked || this.isEditableCostDisabled(index);
    },
    getEditableCostIconButton(index) {
      return this.data.logisticsCosts[index].isProductEditableCost ? "mdi-lock-outline" : "mdi-lock-off-outline";
    },
    updateEditableCostStatus(period, index) {
      if (this.isPriceInteractionLocked) return;
      const cost = this.data.logisticsCosts[index];
      cost.isProductEditableCost = !cost.isProductEditableCost;

      if (cost.isProductEditableCost) {
        cost.productCost = {
          type: "custom",
          currency: this.currentCurrency,
          value: {
            default: cost.productCost.value.default,
            single: null,
            twin: null,
            triple: null,
            quad: null,
          },
          confirm: false,
        };
        if (this.allowPriceEdits) this.$emit("price-override-changed");
        return;
      }

      const supplierId = cost.supplier.value;
      const productId = cost.product.value;

      if (!supplierId || !productId) {
        if (this.allowPriceEdits) this.$emit("price-override-changed");
        return;
      }

      const product = this.supplierProductData[cost.supplier.value].products[cost.product.value];
      const productCost = quoteUtils.getProductCost(
        this.environment,
        this.supplierProductData,
        product,
        period.startYear,
        period,
        null,
        null
      );

      cost.productCost = {
        type: productCost.pricingType,
        currency: productCost.currency,
        value: productCost.value,
        valueIncludesPorterage: productCost.valueIncludesPorterage,
        porterage: productCost.porterage,
        confirm: productCost.confirm,
        rateKind: productCost.rateKind,
      };
      cost.productType = product.product_type;
      quoteUtils.applyShowInProgramForProductType(cost);
      if (this.allowPriceEdits) this.$emit("price-override-changed");
    },
    getSupplierContextKey(itemUuid) {
      return getSupplierContextKey("logistics", itemUuid);
    },
    getSupplierDropdownEntry(contextKey) {
      return getSupplierDropdownEntry(this.supplierDropdownState, contextKey);
    },
    isSupplierLoading(contextKey) {
      return isSupplierLoading(this.supplierDropdownState, contextKey);
    },
    hasMoreSuppliers(contextKey) {
      return hasMoreSuppliers(this.supplierDropdownState, contextKey);
    },
    getSupplierItems(period, productType, contextKey, selectedSupplier) {
      const supplierType = quoteUtils.getSupplierTypeByProductType(productType);
      const dropdownState = this.getSupplierDropdownEntry(contextKey);
      const suppliers = getSupplierEntriesByContext(this.supplierProductData, dropdownState, selectedSupplier);

      const filtered = suppliers.filter(({ supplier }) => {
        const { active, supplier_type, meta, product_rates, search_types, id } = supplier;

        // If the supplier is not active then skip it
        if (!active) return false;

        // If the supplier is TBD then include it
        if (TBD_SUPPLIER_IDS.includes(id)) return true;

        // Filter only transport and tour guide supplier
        if (!["TRANSPORT", "TOUR_GUIDE"].includes(supplier_type)) return false;

        // If the user fill product type field then filter by supplier type
        if (supplierType) {
          const supplierTypes = [supplier_type, ...(search_types || [])];
          if (!supplierTypes.includes(supplierType)) return false;
        }

        // ** If the product_rates is not available then still include it
        if (!product_rates) return true;

        // Pick the rate for the current year or the latest available year if current year is not available
        const rateYear = Number(period.startYear);
        const fallbackRateYear = Math.max(...Object.keys(product_rates).map(Number));
        const rate = product_rates[rateYear] ?? product_rates[fallbackRateYear];

        // ** If the rate is not available then still include it
        if (!rate) return true;

        // If rate are not includes these codes then skip it
        if (!["PG", "PD"].some((code) => rate.includes(code))) return false;

        return true;
      });

      return mapGroupedSupplierItems(filtered);
    },
    getProductItemsBySupplier(supplier, productType, period) {
      if (supplier == null) return [];
      if (supplier.value == null) return [];
      if (this.supplierProductData[supplier.value]?.products == null) return [];

      const supplierId = supplier.value;
      const products = this.supplierProductData[supplierId].products;

      const filtered = Object.values(products).filter((product) => {
        const { active, product_type } = product;

        // If the product is not active then skip it
        if (!active) return false;

        // Filter only tour and transport product
        if (!["TOUR", "TRANS", "TOUR_LEADER"].includes(product_type)) return false;

        // If the user fill product type field then filter by product type
        if (productType && productType != product_type) return false;

        // Get the rate history
        const rateHistory = this.supplierProductData[supplierId].products[product.id].rate_history;
        if (rateHistory == null || rateHistory.rate == null) return true; // If no rate history or no rate, keep it

        // Filter by specific rate types
        const specificRateTypes = ["PD", "PG"];
        if (rateHistory.seasonal) {
          return rateHistory.rate.some((entry) => specificRateTypes.includes(entry?.rate?.type));
        } else {
          return specificRateTypes.includes(rateHistory.rate.type);
        }
      });

      // Group by product_type
      const grouped = {};
      filtered.forEach((product) => {
        const group = product.product_type || "Unknown";
        if (!grouped[group]) grouped[group] = [];
        grouped[group].push({ label: product.name, value: product.id });
      });

      // Flatten with headers
      const result = [];
      Object.entries(grouped).forEach(([type, items]) => {
        result.push({ header: type });
        result.push(...items);
      });

      return result;
    },
    onSupplierChanged(index, v, productType) {
      if (this.readonly) return;
      const newLogisticsCost = this.data.logisticsCosts;
      const logisticsCost = newLogisticsCost[index];

      // Clear product, productCost and isProductEditableCost
      logisticsCost.product = null;
      logisticsCost.productCost = {
        type: "custom",
        currency: this.currentCurrency,
        value: {
          default: 0,
          single: null,
          twin: null,
          triple: null,
          quad: null,
        },
        confirm: false,
      };
      logisticsCost.isProductEditableCost = true;

      // Clear supplier and supplierCity if supplier is freetext
      if (v.label === "" && v.value === null) {
        logisticsCost.supplierCity = "";
        logisticsCost.supplier = null;
        this.data = { ...this.data, logisticsCosts: newLogisticsCost };
        return;
      }

      logisticsCost.supplier = v;

      // Show city name if supplier is not freetext
      if (v.value !== null) {
        logisticsCost.supplierCity = this.supplierProductData[v.value].supplier.city ?? "";
        this.$emit("onSupplierChanged", v.value, productType);
      }

      this.data = { ...this.data, logisticsCosts: newLogisticsCost };
    },
    onSupplierFocus(itemUuid, productType) {
      emitSupplierDropdownEvent(this, "onSupplierFocus", {
        source: "logistics",
        prefix: "logistics",
        itemUuid,
        productType,
      });
    },
    onSupplierSearch(itemUuid, productType, keyword) {
      emitSupplierDropdownEvent(this, "onSupplierSearch", {
        source: "logistics",
        prefix: "logistics",
        itemUuid,
        productType,
        keyword,
      });
    },
    onSupplierLoadMore(itemUuid, productType) {
      emitSupplierDropdownEvent(this, "onSupplierLoadMore", {
        source: "logistics",
        prefix: "logistics",
        itemUuid,
        productType,
      });
    },
    onProductChanged(period, index, v) {
      if (this.readonly) return;
      const { costs, logisticsCosts } = this.data;

      const logisticsCost = logisticsCosts[index];
      if (!logisticsCost || !logisticsCost.uuid) {
        return;
      }

      this.unlinkLogisticsCostUuid(logisticsCost.uuid);

      logisticsCost.product = v;

      const supplierId = logisticsCost.supplier.value;

      if (supplierId == null) {
        return;
      }

      const productId = v.value;
      if (productId == null) {
        logisticsCost.productCost = {
          type: "custom",
          currency: this.currentCurrency,
          value: {
            default: 0,
            single: null,
            twin: null,
            triple: null,
            quad: null,
          },
          confirm: false,
        };
        logisticsCost.isProductEditableCost = true;

        this.data = { ...this.data, logisticsCosts };

        return;
      }

      const product = this.supplierProductData[supplierId].products[productId];
      const productCost = quoteUtils.getProductCost(
        this.environment,
        this.supplierProductData,
        product,
        period.startYear,
        period,
        null,
        null
      );

      logisticsCost.productCost = {
        type: productCost.pricingType,
        currency: productCost.currency,
        value: productCost.value,
        valueIncludesPorterage: productCost.valueIncludesPorterage,
        porterage: productCost.porterage,
        confirm: productCost.confirm,
        rateKind: productCost.rateKind,
      };

      logisticsCost.productType = product.product_type;
      logisticsCost.isProductEditableCost = false;
      quoteUtils.applyShowInProgramForProductType(logisticsCost);

      const days = Array.from({ length: costs.length }, (_, index) => index);
      logisticsCost.days = days;
      costs.forEach((cost, i) => {
        const filteredItems = this.data.costs[i].items.filter((item) =>
          ["ROOM", "MEAL", "EXCURSION"].includes(item.productType)
        );
        filteredItems.forEach((item) => item.logisticsCostUUIDs.push(logisticsCost.uuid));
      });

      this.data = { ...this.data, logisticsCosts };

      this.$emit("onProductTypeChanged", product.product_type);
    },
    onProductFocus(supplier) {
      this.$emit("onProductFocus", supplier.value);
    },
    onProductTypeChanged(index, v, supplier) {
      if (this.readonly) return;
      const newLogisticsCost = this.data.logisticsCosts;
      const logisticsCost = newLogisticsCost[index];
      logisticsCost.product = null;
      logisticsCost.productType = v;
      logisticsCost.days = [];
      quoteUtils.applyShowInProgramForProductType(logisticsCost);
      this.$emit("onProductTypeChanged", v);
      if (supplier != null) {
        this.$emit("onSupplierChanged", supplier.value, logisticsCost.productType);
      }
      this.data = { ...this.data, logisticsCosts: newLogisticsCost };
    },
    redirectToSupplier(supplier) {
      if (supplier?.value == null) return;
      window.open(this.$router.resolve("/suppliers/" + supplier.value).href, "_blank");
    },
    redirectToProduct(product) {
      if (product?.value == null) return;
      window.open(this.$router.resolve("/products/" + product.value).href, "_blank");
    },
    unlinkLogisticsCostUuid(uuid) {
      const { costs } = this.data;
      costs.forEach((cost) => {
        cost.items.forEach((item) => {
          if (Array.isArray(item.logisticsCostUUIDs)) {
            item.logisticsCostUUIDs = item.logisticsCostUUIDs.filter((id) => id !== uuid);
          }
        });
      });
    },
    isSeasonalCost(productCostType) {
      if (this.data.period.startDay) return false;
      return productCostType === "seasonal";
    },
    showRefreshButton(index) {
      // return !this.data.logisticsCosts[index].productCostType;
      return true;
    },
    refreshProductCostType(period, index) {
      if (this.readonly) return;
      const newLogisticsCosts = this.data.logisticsCosts;
      const logisticsCost = newLogisticsCosts[index];
      const isProductEditableCost = logisticsCost.isProductEditableCost;

      if (isProductEditableCost) {
        logisticsCost.productCostType = "custom";
        logisticsCost.productCost.currency = this.currentCurrency;
        return;
      }

      const supplierId = logisticsCost.supplier.value;
      const productId = logisticsCost.product.value;

      if (!supplierId || !productId) return;

      const product = this.supplierProductData[supplierId].products[productId];
      const productCost = quoteUtils.getProductCost(
        this.environment,
        this.supplierProductData,
        product,
        period.startYear,
        period,
        null,
        null
      );

      logisticsCost.productCost = {
        type: productCost.pricingType,
        currency: productCost.currency,
        value: productCost.value,
        valueIncludesPorterage: productCost.valueIncludesPorterage,
        porterage: productCost.porterage,
        confirm: productCost.confirm,
        rateKind: productCost.rateKind,
      };
      logisticsCost.productType = product.product_type;
      quoteUtils.applyShowInProgramForProductType(logisticsCost);

      this.data = { ...this.data, logisticsCosts: newLogisticsCosts };
    },
  },
};
</script>

<style scoped>
.quote-section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.quote-section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
}

.quote-section-action-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  height: 28px !important;
  min-height: 28px !important;
}

.quote-section-block {
  display: block;
  width: 100%;
  min-width: 720px;
  max-width: 100%;
  margin-top: 10px;
  padding: 12px 14px 10px;
  border: 1px solid #d0d0d0;
  border-radius: 8px;
  background: #fff;
  box-sizing: border-box;
}

.quote-section-products {
  overflow-x: auto;
  width: 100%;
}

.quote-section-table {
  width: 100%;
  min-width: 1100px;
  border-collapse: collapse;
}

.quote-section-table th {
  padding: 6px 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #666;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
  white-space: nowrap;
}

.quote-section-table td {
  padding: 6px 8px;
  vertical-align: middle;
  border-bottom: 1px solid #efefef;
}

.quote-section-table__row:hover td {
  background: #fafafa;
}

.quote-cost-field--missing >>> .v-text-field--outlined fieldset {
  border-color: #e53935 !important;
}

.quote-product-type-row td:first-child {
  box-shadow: inset 4px 0 0 var(--quote-product-type-accent);
}

.quote-section-table__empty-row td {
  height: 40px;
  padding: 10px 8px;
  border-bottom: 1px solid #efefef;
}

.quote-section-empty-label {
  font-size: 12px;
  color: #9e9e9e;
}

.quote-section-table__reorder {
  width: 56px;
  white-space: nowrap;
}

.quote-section-table__icon-cell {
  width: 28px;
  padding-left: 2px !important;
  padding-right: 2px !important;
  text-align: center;
}

.quote-section-table__unit,
.quote-section-table__program {
  text-align: center;
  min-width: 72px;
  vertical-align: middle;
}

.quote-program-toggle {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: 24px;
}

.quote-program-toggle >>> .v-input--selection-controls,
.quote-program-toggle >>> .v-input--switch {
  margin: 0;
  padding: 0;
  flex: 0 0 auto;
}

.quote-program-toggle >>> .v-input--switch {
  transform: scale(0.82);
  transform-origin: center center;
}

.quote-program-toggle >>> .v-input__slot {
  margin: 0;
}

.quote-section-table__actions {
  width: 36px;
}

.quote-section-table__footer td {
  border-bottom: none;
  padding-top: 4px;
  padding-bottom: 2px;
}

.quote-section-inline-field {
  display: flex;
  align-items: center;
  gap: 2px;
  min-width: 160px;
}

.quote-section-type-field {
  width: 170px;
}

.quote-section-cost-type-field {
  width: 130px;
}

.quote-section-warning-icon {
  height: 14px;
  width: 14px;
}

.quote-section-add-line {
  text-transform: none !important;
  letter-spacing: 0 !important;
  padding-left: 0 !important;
}
</style>
